import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { subscribeToUserStatuses, unsubscribeFromUserStatuses } from '../store/features/StatusSubscription/statusSubscriptionSlice';
import { useGetStatusesQuery, UserStatus } from '../store/features/Team/teamService';
import { selectSubscribedUserIds } from '../store/features/StatusSubscription/statusSubscriptionSlice';
import { useSelector } from 'react-redux';

interface UseStatusSubscriptionOptions {
    userIds?: (string | number)[];
}

/**
 * Hook to subscribe to status updates for specific users.
 * Components call this hook with their needed user IDs.
 * The StatusProvider at app root fetches statuses for all subscribed users.
 * This hook returns the status data from the shared RTK Query cache.
 */
export const useStatusSubscription = (options: UseStatusSubscriptionOptions = {}) => {
    const { userIds = [] } = options;
    const dispatch = useDispatch();

    // Normalize user IDs to strings and create a stable string key for comparison
    const normalizedUserIdsString = useMemo(() => {
        const normalized = userIds
            .map(id => String(id))
            .filter(id => id && id.trim() !== '' && id !== 'undefined' && id !== 'null')
            .sort(); // Sort to ensure consistent ordering
        return normalized.join(',');
    }, [userIds]);

    // Subscribe to status updates for these users
    useEffect(() => {
        const idsToSubscribe = normalizedUserIdsString ? normalizedUserIdsString.split(',') : [];

        if (idsToSubscribe.length > 0) {
            dispatch(subscribeToUserStatuses(idsToSubscribe));
        }

        // Cleanup: unsubscribe when component unmounts or userIds change
        return () => {
            if (idsToSubscribe.length > 0) {
                dispatch(unsubscribeFromUserStatuses(idsToSubscribe));
            }
        };
    }, [normalizedUserIdsString, dispatch]); // Use string for comparison, not array

    // Get all subscribed user IDs from Redux
    const subscribedUserIds = useSelector(selectSubscribedUserIds);

    // Access the cached status data (StatusProvider is actually fetching it)
    const { data: statuses = [], isLoading, isError, refetch } = useGetStatusesQuery(
        { users: subscribedUserIds },
        {
            skip: subscribedUserIds.length === 0
        }
    );

    const getStatus = useCallback((userId: string | number | undefined): UserStatus | undefined => {
        if (!userId && userId !== 0) return undefined;
        return statuses.find(status => String(status.userId) === String(userId));
    }, [statuses]);

    return {
        statuses,
        getStatus,
        isLoading,
        isError,
        refetch
    };
};

export default useStatusSubscription;
